import { useEffect, useState } from 'react';
import type { CalendarDateTime } from '@internationalized/date';
import {
  Button,
  Card,
  Chip,
  Description,
  Drawer,
  Label,
  Separator,
  Tabs,
  useOverlayState,
  type OverlayTriggerState,
} from '@heroui/react';
import { MapPin, X } from 'lucide-react';
import { FloorPlanDrawer } from '../../../components/floor-plan/floor-plan-drawer';
import {
  floorPlanRectFromLocation,
  hasFloorPlanLocation,
} from '../../../components/floor-plan/floor-plan-utils';
import type { AvailabilityWindowInfo, AvailableSpot } from '../../../api/available-spots';
import type { CreateSpotUsageRequest } from '../../../api/available-spots';
import type { SpotUsage, UsageType } from '../../../api/my-parking-spots';
import { AsyncButton } from '../../../components/ui/async-button';
import { InlineDateTimeSection } from '../../../components/ui/inline-datetime-section';
import { primaryPageTabClassName } from '../../../components/ui/page-tab-classes';
import {
  drawerBodyClassName,
  drawerFooterClassName,
  sideDrawerDialogClassName,
} from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { DateTimeFrame } from '../../../components/ui/datetime-frame';
import { UsageTypeChip } from '../../../components/ui/usage-chips';
import { UsageTypeRadioGroup } from '../../../components/ui/usage-type-radio-group';
import {
  addMinutesToCalendarDateTime,
  calendarDateTimeToIsoUtc,
  compareCalendarDateTime,
  formatDateBR,
  formatDateTimeBR,
  formatTimeBR,
  getCalendarEndWhenStartChanges,
  getCurrentCalendarDateTime,
  isoUtcToCalendarDateTime,
} from '../../../utils/date-utils';
import {
  canRequestUsage,
  getPendingUsageForPeriod,
  getUsageForWindow,
} from '../utils/usage-matching';

type PeriodMode = 'full' | 'partial';

interface RequestUsageDrawerProps {
  state: OverlayTriggerState;
  spot: AvailableSpot | null;
  myUsages: SpotUsage[];
  onSubmit: (request: CreateSpotUsageRequest) => Promise<void>;
  onCancelUsage: (usageId: string) => Promise<void>;
}

interface UsageFormState {
  startAt: CalendarDateTime | null;
  endAt: CalendarDateTime | null;
  type: UsageType;
}

function emptyForm(type: UsageType = 'BORROW'): UsageFormState {
  return { startAt: null, endAt: null, type };
}

function defaultTypeForWindow(window: AvailabilityWindowInfo): UsageType {
  return window.allowBorrow ? 'BORROW' : 'RENT';
}

function allowedTypesForWindow(window: AvailabilityWindowInfo): UsageType[] {
  const types: UsageType[] = [];
  if (window.allowBorrow) types.push('BORROW');
  if (window.allowRent) types.push('RENT');
  return types;
}

function periodFromWindow(window: AvailabilityWindowInfo): {
  startAt: CalendarDateTime;
  endAt: CalendarDateTime;
} | null {
  const windowStart = isoUtcToCalendarDateTime(window.startAt);
  const windowEnd = isoUtcToCalendarDateTime(window.endAt);
  if (!windowStart || !windowEnd) return null;

  const now = getCurrentCalendarDateTime();
  let startAt = compareCalendarDateTime(now, windowStart) > 0 ? now : windowStart;
  if (compareCalendarDateTime(startAt, windowEnd) >= 0) {
    startAt = windowStart;
  }

  return { startAt, endAt: windowEnd };
}

function existingUsageForWindow(
  myUsages: SpotUsage[],
  spotId: string,
  window: AvailabilityWindowInfo,
): SpotUsage | undefined {
  return (
    getUsageForWindow(myUsages, window.id) ??
    getPendingUsageForPeriod(myUsages, spotId, window.startAt, window.endAt)
  );
}

export function RequestUsageDrawer({
  state,
  spot,
  myUsages,
  onSubmit,
  onCancelUsage,
}: RequestUsageDrawerProps) {
  const [selectedWindow, setSelectedWindow] = useState<AvailabilityWindowInfo | null>(null);
  const [periodMode, setPeriodMode] = useState<PeriodMode | null>(null);
  const [form, setForm] = useState<UsageFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locationDrawer = useOverlayState();

  const resetDrawer = () => {
    setSelectedWindow(null);
    setPeriodMode(null);
    setForm(emptyForm());
    setError(null);
    setSubmitting(false);
    setCancellingId(null);
  };

  useEffect(() => {
    if (!state.isOpen) return;
    resetDrawer();
  }, [state.isOpen, spot?.id]);

  const handleSelectWindow = (window: AvailabilityWindowInfo) => {
    setSelectedWindow(window);
    setPeriodMode(null);
    setForm({
      startAt: null,
      endAt: null,
      type: defaultTypeForWindow(window),
    });
    setError(null);
  };

  const handleSelectPeriodMode = (mode: PeriodMode) => {
    if (!selectedWindow) return;

    const period = periodFromWindow(selectedWindow);
    if (!period) {
      setError('Período inválido para esta disponibilidade.');
      return;
    }

    setPeriodMode(mode);
    setForm((prev) => ({
      ...prev,
      startAt: period.startAt,
      endAt: period.endAt,
      type: defaultTypeForWindow(selectedWindow),
    }));
    setError(null);
  };

  const handleCancelPending = async (usageId: string) => {
    setCancellingId(usageId);
    setError(null);
    try {
      await onCancelUsage(usageId);
      resetDrawer();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao cancelar solicitação.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!spot || !selectedWindow || !periodMode) return;

    if (!form.startAt || !form.endAt) {
      setError('Data de início e fim são obrigatórias.');
      return;
    }

    const minEnd = addMinutesToCalendarDateTime(form.startAt, 1);
    if (compareCalendarDateTime(form.endAt, minEnd) < 0) {
      setError('O fim deve ser pelo menos 1 minuto após o início.');
      return;
    }

    const windowStart = isoUtcToCalendarDateTime(selectedWindow.startAt);
    const windowEnd = isoUtcToCalendarDateTime(selectedWindow.endAt);
    if (!windowStart || !windowEnd) {
      setError('Erro ao validar período da janela.');
      return;
    }

    if (compareCalendarDateTime(form.startAt, windowStart) < 0) {
      setError('O início deve estar dentro da janela de disponibilidade.');
      return;
    }
    if (compareCalendarDateTime(form.endAt, windowEnd) > 0) {
      setError('O fim deve estar dentro da janela de disponibilidade.');
      return;
    }

    const startAtISO = calendarDateTimeToIsoUtc(form.startAt);
    const endAtISO = calendarDateTimeToIsoUtc(form.endAt);

    const existingForWindow = getUsageForWindow(myUsages, selectedWindow.id);
    if (existingForWindow?.status === 'PENDING') {
      setError('Você já possui uma solicitação pendente para este período.');
      return;
    }

    const overlapping = getPendingUsageForPeriod(
      myUsages,
      spot.id,
      startAtISO,
      endAtISO,
    );
    if (overlapping) {
      setError('Você já possui uma solicitação pendente que se sobrepõe a este período.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        availabilityWindowId: selectedWindow.id,
        startAt: startAtISO,
        endAt: endAtISO,
        type: form.type,
      });
      resetDrawer();
      state.close();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao solicitar uso da vaga.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedExistingUsage = selectedWindow
    ? existingUsageForWindow(myUsages, spot?.id ?? '', selectedWindow)
    : undefined;
  const isPendingSelected = selectedExistingUsage?.status === 'PENDING';
  const canConfigure =
    !!selectedWindow && !spot?.isMine && !isPendingSelected && !!periodMode;
  const canSubmit =
    canConfigure &&
    !!form.startAt &&
    !!form.endAt &&
    (selectedWindow?.allowBorrow || selectedWindow?.allowRent);

  const spotMeta = spot
    ? [spot.blockName, spot.level, spot.location].filter(Boolean).join(' · ')
    : '';
  const allowedTypes = selectedWindow ? allowedTypesForWindow(selectedWindow) : [];
  const showTypeGroup = canConfigure && allowedTypes.length > 1;

  return (
    <>
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>
                {spot ? `Solicitar · ${spot.code}` : 'Solicitar uso'}
              </Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              {spot ? (
                <Description>
                  Vaga: <strong>{spot.code}</strong>
                  {spotMeta ? ` · ${spotMeta}` : ''}
                </Description>
              ) : null}

              {spot && hasFloorPlanLocation(spot) ? (
                <Button variant="secondary" onPress={locationDrawer.open}>
                  <MapPin size={16} />
                  Ver localização da vaga
                </Button>
              ) : null}

              {error ? <StatusCallout status="danger">{error}</StatusCallout> : null}

              {spot?.isMine ? (
                <StatusCallout status="warning">
                  Esta é a sua vaga — não é possível solicitar uso.
                </StatusCallout>
              ) : (
                <Description>Escolha a disponibilidade que deseja solicitar.</Description>
              )}

              {spot?.availabilityWindows.map((window) => {
                const existingUsage = existingUsageForWindow(myUsages, spot.id, window);
                const isPending = existingUsage?.status === 'PENDING';
                const isActive = existingUsage?.status === 'ACTIVE';
                const canRequest = canRequestUsage(existingUsage, !!spot.isMine);
                const isSelected = selectedWindow?.id === window.id;

                return (
                  <WindowCard
                    key={window.id}
                    window={window}
                    isSelected={isSelected}
                    isPending={isPending}
                    isActive={isActive}
                    canRequest={canRequest}
                    cancelling={cancellingId === existingUsage?.id}
                    onSelect={() => handleSelectWindow(window)}
                    onCancelPending={
                      existingUsage ? () => handleCancelPending(existingUsage.id) : undefined
                    }
                  />
                );
              })}

              {selectedWindow && !isPendingSelected && !spot?.isMine ? (
                <section className="flex flex-col gap-3">
                  <Separator />
                  <div className="flex flex-col gap-1">
                    <Label className="text-base font-semibold">Como deseja usar?</Label>
                    <Description>
                      Período completo da disponibilidade ou só uma parte?
                    </Description>
                  </div>

                  <Tabs
                    selectedKey={periodMode}
                    onSelectionChange={(key) => {
                      const selected = String(key);
                      if (selected === 'full' || selected === 'partial') {
                        handleSelectPeriodMode(selected);
                      }
                    }}
                  >
                    <Tabs.ListContainer>
                      <Tabs.List aria-label="Tipo de período" className="w-full p-1.5">
                        <Tabs.Tab id="full" className={primaryPageTabClassName}>
                          Completo
                          <Tabs.Indicator />
                        </Tabs.Tab>
                        <Tabs.Tab id="partial" className={primaryPageTabClassName}>
                          Parcial
                          <Tabs.Indicator />
                        </Tabs.Tab>
                      </Tabs.List>
                    </Tabs.ListContainer>

                    <Tabs.Panel id="full" className="pt-3">
                      {periodMode === 'full' && form.startAt && form.endAt ? (
                        <Description>
                          De{' '}
                          <strong>
                            {formatDateTimeBR(calendarDateTimeToIsoUtc(form.startAt))}
                          </strong>{' '}
                          até{' '}
                          <strong>
                            {formatDateTimeBR(calendarDateTimeToIsoUtc(form.endAt))}
                          </strong>
                        </Description>
                      ) : null}
                    </Tabs.Panel>

                    <Tabs.Panel id="partial" className="pt-3">
                      {canConfigure &&
                      periodMode === 'partial' &&
                      form.startAt &&
                      form.endAt ? (
                        <div className="space-y-6">
                          <InlineDateTimeSection
                            title="Data e hora de início"
                            value={form.startAt}
                            minValue={
                              isoUtcToCalendarDateTime(selectedWindow.startAt) ?? undefined
                            }
                            maxValue={
                              isoUtcToCalendarDateTime(selectedWindow.endAt) ?? undefined
                            }
                            onChange={(startAt) =>
                              setForm((prev) => ({
                                ...prev,
                                startAt,
                                endAt: getCalendarEndWhenStartChanges(startAt, prev.endAt),
                              }))
                            }
                          />
                          <InlineDateTimeSection
                            title="Data e hora de fim"
                            value={form.endAt}
                            minValue={
                              form.startAt
                                ? addMinutesToCalendarDateTime(form.startAt, 1)
                                : isoUtcToCalendarDateTime(selectedWindow.startAt) ?? undefined
                            }
                            maxValue={
                              isoUtcToCalendarDateTime(selectedWindow.endAt) ?? undefined
                            }
                            onChange={(endAt) => setForm((prev) => ({ ...prev, endAt }))}
                          />
                        </div>
                      ) : null}
                    </Tabs.Panel>
                  </Tabs>
                </section>
              ) : null}

              {showTypeGroup ? (
                <UsageTypeRadioGroup
                  label="Tipo de uso"
                  description="Selecione o tipo permitido nesta janela."
                  value={form.type}
                  onChange={(type) => setForm((prev) => ({ ...prev, type }))}
                  allowedTypes={allowedTypes}
                  isDisabled={submitting}
                />
              ) : null}
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button variant="secondary" onPress={() => state.close()}>
                Fechar
              </Button>
              <AsyncButton
                variant="primary"
                isPending={submitting}
                isDisabled={!canSubmit}
                onPress={handleSubmit}
              >
                Solicitar uso
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>

    <FloorPlanDrawer
      state={locationDrawer}
      title={spot ? `Localização da vaga ${spot.code}` : 'Localização da vaga'}
      subtitle="Visualização da vaga na planta do condomínio"
      rect={spot ? floorPlanRectFromLocation(spot) : null}
    />
    </>
  );
}

function WindowCard({
  window,
  isSelected,
  isPending,
  isActive,
  canRequest,
  cancelling,
  onSelect,
  onCancelPending,
}: {
  window: AvailabilityWindowInfo;
  isSelected: boolean;
  isPending: boolean;
  isActive: boolean;
  canRequest: boolean;
  cancelling: boolean;
  onSelect: () => void;
  onCancelPending?: () => void;
}) {
  const isPressable = canRequest;

  return (
    <Card
      variant={isSelected ? 'default' : 'secondary'}
      className={isSelected ? 'ring-2 ring-accent' : undefined}
    >
      <Card.Content
        className={['flex flex-col gap-3 p-4', isPressable ? 'cursor-pointer' : '']
          .filter(Boolean)
          .join(' ')}
        role={isPressable ? 'button' : undefined}
        tabIndex={isPressable ? 0 : undefined}
        aria-pressed={isPressable ? isSelected : undefined}
        aria-label={
          isPressable
            ? `Disponibilidade de ${formatDateBR(window.startAt)} ${formatTimeBR(window.startAt)} até ${formatDateBR(window.endAt)} ${formatTimeBR(window.endAt)}`
            : undefined
        }
        onClick={isPressable ? onSelect : undefined}
        onKeyDown={
          isPressable
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect();
                }
              }
            : undefined
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <DateTimeFrame label="Início" iso={window.startAt} />
          <DateTimeFrame label="Fim" iso={window.endAt} />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {window.allowBorrow ? <UsageTypeChip type="BORROW" /> : null}
          {window.allowRent ? <UsageTypeChip type="RENT" /> : null}
          {isPending ? (
            <Chip size="sm" variant="soft" color="warning">
              Pendente
            </Chip>
          ) : null}
          {isActive ? (
            <Chip size="sm" variant="soft" color="success">
              Em uso
            </Chip>
          ) : null}
        </div>

        {isPending && onCancelPending ? (
          <div
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <AsyncButton
              size="sm"
              variant="danger-soft"
              className="w-full"
              isPending={cancelling}
              onPress={onCancelPending}
            >
              <X size={14} />
              Cancelar solicitação
            </AsyncButton>
          </div>
        ) : null}

        {!canRequest && !isPending ? (
          <Description className="text-sm">
            {isActive ? 'Você já está usando este período.' : 'Solicitação já processada.'}
          </Description>
        ) : null}
      </Card.Content>
    </Card>
  );
}
